<template>
  <div>
    <table class="table table-bordered table-condensed">
      <thead>
        <tr>
          <th>ID</th>
          <th>Added date</th>
          <th>Composition</th>
          <th>Total materials [{{getWeightUnit}}]</th>
          <th style="width: 160px;">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(data, key, index) in getFilteredComputedApplications" :key="data.id">
          <td>{{ index + 1 }}</td>
          <td>{{ formatAddedDate(data.createdAt) }}</td>
          <td>{{ data.compositionName }}</td>
          <td>{{ volumeByWeightUnit(data.applicationLinksSum) }}</td>
          <td>
            <span class="action-icon" @click="previewAction(data)" title="Preview application">
              <i class="fas fa-eye"></i>
            </span>
            <span class="action-icon" @click="updateAction(data, index)" title="Edit application">
              <i class="fas fa-pen"></i>
            </span>
            <span class="action-icon" @click="removeAction(data)" title="Delete application">
              <i class="fas fa-ban"></i>
            </span>
          </td>
        </tr>
        <tr>
          <td class="text-right b-top-2" colspan="3">Total added:</td>
          <td class="b-top-2">{{ volumeByWeightUnit(totalAdded) }}</td>
          <td class="b-top-2">&nbsp;</td>
        </tr>
        <tr>
          <td class="text-right" colspan="3">Sum in submissions:</td>
          <td>{{ volumeByWeightUnit(sumInSubmissions) }}</td>
          <td>&nbsp;</td>
        </tr>
        <tr>
          <td class="text-right" colspan="3">Remaining:</td>
          <td>{{ volumeByWeightUnit(remainingMaterials) }}</td>
          <td>&nbsp;</td>
        </tr>
      </tbody>
    </table>
    <hr class="mt-5 mb-5">
    <h5 class="mb-3">Select composition to add new application.</h5>
    <div class="d-flex justify-content-around mb-5 r-composition row">
      <div
        class="text-center col-3 btn mt-3 mb-2"
        style="cursor: pointer"
        v-for="(composition, key, index) in groupedUserCompositions"
        :key="key"
        @click="setCurrentComposition(composition)"
      >
        <application-composition-cylinder
          :id="key"
          :index="index"
          :compositionName="composition.name"
          :total="composition.remainingVolume"
          :selected="currentComposition.id == key"
          :subtracted="currentComposition.id == key ? currentComposition.subtracted : 0"
        />
        <!-- <application-composition-bottle
          :id="key"
          :compositionName="composition.name"
          :total="composition.remainingVolume"
          :selected="currentComposition.id == key"
          :subtracted="currentComposition.id == key ? currentComposition.subtracted : 0"
          @deleteComposition="handleDeleteComposition"
        /> -->
      </div>
    </div>
    <form @submit.prevent="onSubmit()">
      <base-checkbox
        v-show="currentComposition.id"
        v-model="inPercentage"
        :value="inPercentage"
        label="Materials in percentage"
      />
      <applications-tree
        v-show="currentComposition.id"
        :data="treeApplications"
        :max="treeApplicationsRemainingMaterials"
        :remaining="treeApplicationsRemainingMaterials"
        :sliderStatusId="sliderStatusId"
        :sumInSliders="sumInSliders"
        :inPercentage="inPercentage"
      />
      <div class="row">
        <div class="col-md-6">
          <base-button radius="bl" component-class="blue" @click.native="$router.go(-1)">
            <i class="fas fa-arrow-left mr-3"></i> Back
          </base-button>
        </div>
        <div class="col-md-6 text-right">
          <base-button
            :disabled="isSliderSumHighQty"
            v-if="sliderStatusId !== -1"
            radius="br"
            component-class="green filled"
            type="submit"
          >Add</base-button>
        </div>
      </div>
    </form>
    <modal
      name="preview-application-link"
      height="auto"
      :scrollable="true"
    >
      <div class="modal-header">
        <h4 class="modal-title">
          {{selectedApplicationName}}
        </h4>
      </div>
      <div class="modal-body">
        <div class="row">
          <div class="col-12">
          <table class="table table-bordered table-condensed">
            <thead>
              <tr>
                <th>Name</th>
                <th>Volume [{{getWeightUnit}}]</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(application, key) in selectedApplicationLinks" :key="key">
                <td>{{ application.name }}</td>
                <td>{{ application.volume }}</td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <base-button radius="bl" component-class="blue" @click.native="$modal.hide('preview-application-link')">
          Close
        </base-button>
      </div>
  </modal>
  </div>
</template>

<script>
  import * as moment from 'moment';
  import { mapState, mapActions, mapMutations } from 'vuex';
  import ApplicationCompositionCylinder from '../ApplicationCompositionCylinder';
  import BaseButton from '@/components/inputs/button/BaseButton';
  import BaseCheckbox from '@/components/inputs/checkbox/BaseCheckbox';
  import ApplicationsTree from '@/components/inputs/tree/ApplicationsTree';

  export default {
    props: {
      computedApplications: {
        type: Object,
        required: true,
      },
      groupedUserCompositions: {
        type: Object,
        required: true,
      },
      totalAdded: {
        type: Number,
        default: 0,
      },
      sumInSubmissions: {
        type: Number,
        default: 0,
      },
      remainingMaterials: {
        type: Number,
        default: 0,
      },
      sumInSliders: {
        type: Number,
        default: 0,
      },
      company: {
        type: Object,
        default: () => ({ id: 0 })
      },
      year: {
        type: String,
        default: ''
      }
    },

    data() {
      return {
        currentComposition: {
          id: 0,
          name: '',
          subtracted: 0,
          total: 0
        },
        sliderStatusId: -1,
        selectedApplicationName: '',
        selectedApplicationLinks: {},
        inPercentage: false
      };
    },

    computed: {
      ...mapState({
        userCompanies: state => state.userCompanies.loggedUserCompanies,
        userSubmissions: state => state.userSubmissions.data,
      }),
      getWeightUnit() {
        return this.$store.getters['user/getWeightUnit'];
      },
      getFilteredComputedApplications() {
        const filteredObj = {};
        Object.keys(this.computedApplications)
          .forEach(key => {
            if (
              this.computedApplications[key].companyId === this.company.id
              && this.computedApplications[key].createdAt.substr(0, 4) === this.year.toString()
            ) {
              filteredObj[key] = this.computedApplications[key];
            }
          });
        return filteredObj;
      },
      isSliderSumHighQty() {
        if (this.treeApplicationsRemainingMaterials === 0) {
          this.$emit('isHighQty', true);
          return true;
        }

        const isHighQty = this.sumInSliders > this.treeApplicationsRemainingMaterials;
        this.$emit('isHighQty', isHighQty);
        return isHighQty;
      },
      treeApplications() {
        return this.$store.getters['applications/treeApplications'];
      },
      treeApplicationsRemainingMaterials() {
        if (this.currentComposition.total > this.remainingMaterials) {
          return this.remainingMaterials;
        }
        return this.currentComposition.total;
      }
    },

    watch: {
      sumInSliders(newValue) {
        this.currentComposition.subtracted = newValue;
      },
    },

    methods: {
      ...mapActions({
        getApplicationLinks: 'userApplications/getApplicationLinks',
        // getSubmissions: 'userSubmissions/getSubmissions',
        removeUserComposition: 'userCompositions/remove',
        // removeSubmission: 'userSubmissions/remove',
      }),
      ...mapMutations({
        setCurrentComposition: 'applicationModule/setCurrentComposition',
        setCurrentApplication: 'applicationModule/setCurrentApplication',
      }),
      volumeByWeightUnit(volume) {
        if (this.getWeightUnit === 'ton') {
          return volume / 1000;
        }
        return volume;
      },
      resetCurrentComposition() {
        this.currentComposition = {
          id: 0,
          name: '',
          subtracted: 0,
          total: 0
        };
      },
      resetApplicationsTree() {
        this.$store.commit('userApplications/current', {
          application_id: 0,
          applications: []
        });
      },
      previewAction(application) {
        this.selectedApplicationName = application.compositionName;

        const applicationObj = {};
        const traverse = (applications) => {
          applications.forEach((application) => {
            if (typeof application.children !== 'undefined' && application.children.length > 0) {
              traverse(application.children);
            }

            applicationObj[application.id] = application.name;
          });
        };
        traverse(this.treeApplications);

        const linksObj = {};
        application.applicationsLink.forEach((link) => {
          linksObj[link.id] = {
            name: applicationObj[link.application_id],
            volume: link.volume
          };
        });
        this.selectedApplicationLinks = linksObj;
        this.$modal.show('preview-application-link');
      },
      updateAction(application, index) {
          application.index = index;
          this.setCurrentApplication(application);
          this.$router.push({ name: 'user.applications.edit' });
      },
      async removeAction(application) {
        await this.$store.dispatch('userApplications/remove', application.id);
        this.getApplicationLinks();
        this.resetApplicationsTree();
      },
      async onSubmit() {
        try {
          await this.$store.dispatch(
            'userApplications/save',
            this.currentComposition.id
          );
          $('html, body').animate({
            scrollTop: 0
          }, 800);
          this.resetCurrentComposition();
          this.$nextTick(() => {
            this.getApplicationLinks();
          });
        } catch (error) {
          console.error(error.message);
        }
      },

      setCurrentComposition(composition) {
        this.resetApplicationsTree();
        this.sliderStatusId = 0;
        this.currentComposition = {
          id: composition.id,
          name: composition.name,
          subtracted: 0,
          total: composition.remainingVolume
        };
      },
      formatAddedDate(date) {
        return moment(date).format('YYYY-MM-DD');
      },
      // async handleDeleteComposition({ id }) {
      //   if(confirm('Are you sure to delete composition with all related links and submissions?')) {
      //     try {
      //       const linksIdsToRemove = Object.keys(this.computedApplications).filter(key => this.computedApplications[key].compositionId === id);
      //       await this.getSubmissions(this.company.id);
      //       const submissionsToRemove = this.userSubmissions.filter(submission => submission.composition_id === id);

      //       const linksRemovePromises = linksIdsToRemove.map(id => this.$store.dispatch('userApplications/remove', id));
      //       const submissionsRemovePromises = submissionsToRemove.map(submission => this.removeSubmission(submission.id));
      //       this.resetCurrentComposition();
      //       await Promise.all(linksRemovePromises);
      //       await Promise.all(submissionsRemovePromises);
      //       this.getApplicationLinks();
      //     } catch (error) {
      //       console.log(error);
      //     }
      //   }
      // }
    },

    mounted() {
        this.resetApplicationsTree();
    },

    components: {
      'base-button': BaseButton,
      'base-checkbox': BaseCheckbox,
      'applications-tree': ApplicationsTree,
      'application-composition-cylinder': ApplicationCompositionCylinder,
    }
  };
</script>
